import React, { useEffect, useState } from "react";
import {
    Typography,
    Box,
    CircularProgress,
    Divider,
    useTheme,
    Tabs,
    Tab,
    Button,
    Tooltip,
    Card,
    CardContent,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Switch,
    FormControlLabel,
    Grid,
    Modal,
    TextField,
    IconButton,
    InputAdornment, // <-- Hinzugefügt für das Auge-Icon
    Menu,
    MenuItem,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import TranslateIcon from '@mui/icons-material/Translate';
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Visibility from '@mui/icons-material/Visibility'; // <-- Hinzugefügt für das Auge-Icon
import VisibilityOff from '@mui/icons-material/VisibilityOff'; // <-- Hinzugefügt für das Auge-Icon
import axios from "axios";
import { useParams } from "react-router-dom";
import ProfileGameStatsExternal from "../Components/ProfileGameStatsExternal";

const FILTERS = {
    catched: { de: "Gefangen", en: "Catched" },
    shiny: { de: "Schillernd", en: "Shiny" },
    perfect: { de: "Perfekt", en: "Perfect" },
    crypto: { de: "Crypto", en: "Crypto" },
    dynamax: { de: "Dynamax", en: "Dynamax" },
    individual: { de: "Individuell", en: "Individual" },
};

const FILTER_WORDS = {
    shiny: { de: "schillernd", en: "shiny" },
    perfect: { de: "4*", en: "4*" },
    crypto: { de: "crypto,erlöst", en: "crypto,purified" },
    dynamax: { de: "dynamax,gigadynamax", en: "dynamax,gigantamax" }
};

const ProfileExtern = () => {
    const { id } = useParams();
    const theme = useTheme();
    const [username, setUsername] = useState("");
    // FEHLER BEHOBEN: useState hinzugefügt
    const [loading, setLoading] = useState(true);
    const [pokedexStats, setPokedexStats] = useState([]);
    const [selectedGen, setSelectedGen] = useState(0);

    const [tradeMode, setTradeMode] = useState("owned");
    const [selectedFilter, setSelectedFilter] = useState(null);
    const [tradeData, setTradeData] = useState({ result: [], search: "" });
    const [copySuccess, setCopySuccess] = useState(false);
    const [language, setLanguage] = useState("de");

    // Admin states
    const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(false);
    const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");

    // States für Passwort-Sichtbarkeit
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem("token");

                const [userRes, statsRes, currentUserSettingsRes] = await Promise.all([
                    axios.get(`/api/users/extern/${id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    }),
                    axios.get(`/api/users/pokedex-stats/${id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    }),
                    axios.get(`/api/users/settings`, {
                        headers: { Authorization: `Bearer ${token}` },
                    }),
                ]);

                setUsername(userRes.data.username);
                setPokedexStats(statsRes.data);
                setIsCurrentUserAdmin(currentUserSettingsRes.data?.isAdmin === true);

            } catch (error) {
                console.error("Fehler beim Laden der Daten", error);
                if (error.response && error.response.status === 401) {
                    window.location.href = '/login';
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    useEffect(() => {
        const fetchTradeData = async () => {
            try {
                const token = localStorage.getItem("token");
                const query = new URLSearchParams();
                query.append("type", tradeMode);
                if (selectedFilter) query.append("filter", selectedFilter);

                const res = await axios.get(
                    `/api/users/trade-helper/${id}?${query.toString()}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );

                setTradeData(res.data);
            } catch (error) {
                console.error("Fehler beim Laden der Tauschdaten:", error);
            }
        };

        fetchTradeData();
    }, [selectedFilter, tradeMode, id]);

    const handleCopy = async () => {
        const ids = tradeData.result.join(",");
        const condition = selectedFilter ? FILTER_WORDS[selectedFilter]?.[language] : null;
        const searchString = ids + (condition ? `&${condition}` : "");

        try {
            await navigator.clipboard.writeText(searchString);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch {
            alert("Fehler beim Kopieren");
        }
    };

    const handleResetPassword = async () => {
        if (!newPassword || !confirmNewPassword) {
            alert("Bitte gib beide Passwörter ein.");
            return;
        }
        if (newPassword.length < 8) {
            alert("Das Passwort muss mindestens 8 Zeichen lang sein.");
            return;
        }
        if (newPassword !== confirmNewPassword) {
            alert("Die Passwörter stimmen nicht überein.");
            return;
        }

        try {
            const token = localStorage.getItem("token");
            await axios.post(
                `/api/admin/reset-password`,
                { userId: id, newPassword: newPassword },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            alert(`Passwort für ${username} erfolgreich zurückgesetzt!`);
            setShowResetPasswordModal(false);
            setNewPassword("");
            setConfirmNewPassword("");
            setShowNewPassword(false); // Sichtbarkeit zurücksetzen
            setShowConfirmNewPassword(false); // Sichtbarkeit zurücksetzen
        } catch (error) {
            console.error("Fehler beim Zurücksetzen des Passworts:", error);
            const errorMessage = error.response?.data?.error || "Ein unbekannter Fehler ist aufgetreten.";
            alert(`Fehler beim Zurücksetzen des Passworts: ${errorMessage}. Stelle sicher, dass du Admin-Rechte hast.`);
        }
    };

    const handleMenuClick = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleResetPasswordFromMenu = () => {
        setShowResetPasswordModal(true);
        handleMenuClose();
    };

    // Funktionen zum Umschalten der Passwort-Sichtbarkeit
    const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);
    const handleMouseDownNewPassword = (event) => {
        event.preventDefault();
    };
    const handleClickShowConfirmNewPassword = () => setShowConfirmNewPassword((show) => !show);
    const handleMouseDownConfirmNewPassword = (event) => {
        event.preventDefault();
    };


    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={5}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box mt={5} mx={2}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={4}>
                <Typography variant="h4" sx={{ color: theme.palette.text.primary, mr: 2 }}>
                    Profil von {username}
                </Typography>
                {isCurrentUserAdmin && (
                    <Box>
                        <IconButton onClick={handleMenuClick} size="small">
                            <MoreVertIcon />
                        </IconButton>
                        <Menu
                            anchorEl={menuAnchorEl}
                            open={Boolean(menuAnchorEl)}
                            onClose={handleMenuClose}
                        >
                            <MenuItem onClick={handleResetPasswordFromMenu}>
                                Passwort zurücksetzen
                            </MenuItem>
                        </Menu>
                    </Box>
                )}
            </Box>


            <ProfileGameStatsExternal userId={id} />

            {/* Pokédex Statistik */}
            <Box mb={4}>
                <Typography variant="h5" gutterBottom align="center">
                    Pokédex - Statistik
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {pokedexStats.length > 0 && (
                    <>
                        <Tabs
                            value={selectedGen}
                            onChange={(e, newValue) => setSelectedGen(newValue)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{ borderBottom: `1px solid ${theme.palette.divider}`, mb: 2 }}
                        >
                            {pokedexStats.map((gen, index) => (
                                <Tab key={gen.generation} label={`GEN ${gen.generation}`} value={index} />
                            ))}
                        </Tabs>

                        <Box
                            sx={{
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 2,
                                p: 3,
                                backgroundColor: theme.palette.background.paper,
                                boxShadow: 2,
                                textAlign: "center",
                            }}
                        >
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                                Gesamt: {pokedexStats[selectedGen].total_in_generation} Pokémon
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
                                <Typography>Gefangen:</Typography>
                                <Typography>{pokedexStats[selectedGen].catched}</Typography>
                                <Typography>Shiny:</Typography>
                                <Typography>{pokedexStats[selectedGen].shiny}</Typography>
                                <Typography>Perfekt:</Typography>
                                <Typography>{pokedexStats[selectedGen].perfect}</Typography>
                                <Typography>Crypto:</Typography>
                                <Typography>{pokedexStats[selectedGen].crypto}</Typography>
                                <Typography>Dynamax:</Typography>
                                <Typography>{pokedexStats[selectedGen].dynamax}</Typography>
                            </Box>
                        </Box>
                    </>
                )}
            </Box>

            {/* Tauschhelfer */}
            <Box mb={6}>
                <Typography variant="h5" gutterBottom align="center">
                    Tauschhelfer
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Card
                    variant="outlined"
                    sx={{
                        p: 3,
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: 2,
                        maxWidth: 500,
                        mx: "auto",
                    }}
                >
                    <CardContent>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                Sprache: {language === "de" ? "Deutsch" : "English"}
                            </Typography>
                            <FormControlLabel
                                control={<Switch checked={language === "en"} onChange={() => setLanguage((prev) => (prev === "de" ? "en" : "de"))} />}
                                label={<TranslateIcon />}
                            />
                        </Stack>

                        <Tabs
                            value={tradeMode}
                            onChange={(e, newValue) => setTradeMode(newValue)}
                            indicatorColor="primary"
                            textColor="primary"
                            variant="fullWidth"
                            sx={{ mb: 2 }}
                        >
                            <Tab label="Pokémon im Besitz" value="owned" />
                            <Tab label="Fehlende Pokémon" value="missing" />
                        </Tabs>

                        <Grid container spacing={1} justifyContent="center" sx={{ mb: 2 }}>
                            {Object.keys(FILTERS).map((key) => (
                                <Grid item xs={6} key={key}>
                                    <ToggleButtonGroup
                                        color="secondary"
                                        value={selectedFilter === key ? key : null}
                                        exclusive
                                        onChange={() => setSelectedFilter(selectedFilter === key ? null : key)}
                                        fullWidth
                                    >
                                        <ToggleButton value={key}>{FILTERS[key][language]}</ToggleButton>
                                    </ToggleButtonGroup>
                                </Grid>
                            ))}
                        </Grid>

                        <Box display="flex" justifyContent="center" mb={1}>
                            <Tooltip title="Suchstring kopieren">
                                <Button
                                    variant="contained"
                                    color={copySuccess ? "success" : "primary"}
                                    onClick={handleCopy}
                                >
                                    {copySuccess ? "Kopiert!" : "Suchstring kopieren"}
                                </Button>
                            </Tooltip>
                        </Box>

                        <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                            {tradeData.result?.length || 0} Pokémon gefunden
                        </Typography>
                        <Typography variant="caption" align="center" display="block" sx={{ mt: 1 }}>
                            Pokémon GO erlaubt keine logische Kombination aus den einzelnen Suchmöglichkeiten.
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            {/* Passwort zurücksetzen Modal */}
            <Modal
                open={showResetPasswordModal}
                onClose={() => setShowResetPasswordModal(false)}
                aria-labelledby="reset-password-modal-title"
                aria-describedby="reset-password-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '90%', sm: 400 },
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1, // <-- Abstand reduziert
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography id="reset-password-modal-title" variant="h6" component="h2">
                            Passwort für {username} zurücksetzen
                        </Typography>
                        <IconButton onClick={() => setShowResetPasswordModal(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <TextField
                        label="Neues Passwort"
                        type={showNewPassword ? 'text' : 'password'}
                        fullWidth
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        helperText="Mindestens 8 Zeichen"
                        margin="normal"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="Passwort anzeigen/verbergen"
                                        onClick={handleClickShowNewPassword}
                                        onMouseDown={handleMouseDownNewPassword}
                                        edge="end"
                                    >
                                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        label="Neues Passwort bestätigen"
                        type={showConfirmNewPassword ? 'text' : 'password'}
                        fullWidth
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        error={newPassword && confirmNewPassword && newPassword !== confirmNewPassword}
                        helperText={newPassword && confirmNewPassword && newPassword !== confirmNewPassword ? "Passwörter stimmen nicht überein" : ""}
                        margin="normal"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="Passwort anzeigen/verbergen"
                                        onClick={handleClickShowConfirmNewPassword}
                                        onMouseDown={handleMouseDownConfirmNewPassword}
                                        edge="end"
                                    >
                                        {showConfirmNewPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleResetPassword}
                        disabled={newPassword.length < 8 || newPassword !== confirmNewPassword}
                    >
                        Passwort speichern
                    </Button>
                </Box>
            </Modal>
        </Box>
    );
};

export default ProfileExtern;